import { useConversation, type Language } from '@elevenlabs/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLiveKit,
  type VoiceConfig as LiveKitVoiceConfig,
} from './useLiveKit';
import api from '@/utils/ky';

// Types
export type VoiceEngine = 'elevenlabs' | 'openai' | 'livekit';
export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface VoiceEngineConfig extends Omit<
  LiveKitVoiceConfig,
  'roomName' | 'participantName'
> {
  agentId?: string;
  roomName?: string;
  participantName?: string;
  scenario?: string;
  ttsConfig?: {
    stability?: number;
    similarityBoost?: number;
    style?: number;
    speed?: number;
    useSpeakerBoost?: boolean;
  };
}

export interface VoiceEngineCallbacks {
  onConnect?: () => void;
  onError?: (error: any) => void;
  onMessage?: (data: { source: 'ai' | 'user'; message: string }) => void;
  onDebug?: (data: any) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onAgentSpeaking?: (isSpeaking: boolean) => void;
}

export interface VoiceEngineHook {
  // Core functions
  start: (
    config: VoiceEngineConfig,
    callbacks?: VoiceEngineCallbacks,
  ) => Promise<string>;
  stop: () => Promise<void>;
  pause: () => void;
  resume: () => void;

  // State
  status: ConnectionStatus;
  engine: VoiceEngine;
  isRecording: boolean;
  isSpeaking: boolean;
  isPaused: boolean;
  error: Error | null;

  // Utilities
  getCurrentEngine: () => VoiceEngine;
  isEngineSupported: (language: string) => boolean;
  startMicrophone?: () => Promise<void>;
}

// Configuration
const OPENAI_CONFIG = {
  model: 'gpt-realtime', // Latest GPT Realtime model
  voice: 'marin', // Marin voice for Singaporean English accent (can be overridden per session)
  temperature: 0.7,
  maxTokens: 4096,
  turnDetection: {
    type: 'server_vad', // Server-side Voice Activity Detection
    threshold: 0.6, // Higher threshold for better noise handling (0-1, higher = louder audio needed)
    prefix_padding_ms: 300, // Audio included before speech detection
    silence_duration_ms: 800, // Longer silence required to detect speech stop (prevents interruption)
    create_response: true, // Auto-create response when user stops speaking
    interrupt_response: false, // PREVENT user from interrupting AI - let AI finish speaking
  },
};

// Languages that require OpenAI (ElevenLabs doesn't support these)
const OPENAI_REQUIRED_LANGUAGES = ['th'];

// Language mapping for ElevenLabs
// ElevenLabs uses 'zh' for all Chinese variants
// This mapping ensures cmn (Mandarin) and yue (Cantonese) work with ElevenLabs
const ELEVENLABS_LANGUAGE_MAP: Record<string, string> = {
  cmn: 'zh', // Mandarin Chinese -> Chinese
  yue: 'zh', // Cantonese -> Chinese
  'zh-TW': 'zh', // Traditional Chinese (Taiwan) uppercase
  'zh-tw': 'zh', // Traditional Chinese (Taiwan) lowercase
  // Add more mappings here if needed
};

// Language mapping for OpenAI
// OpenAI uses 'zh' for all Chinese variants
const OPENAI_LANGUAGE_MAP: Record<string, string> = {
  cmn: 'zh', // Mandarin Chinese -> Chinese
  yue: 'zh', // Cantonese -> Chinese
  'zh-TW': 'zh', // Traditional Chinese (Taiwan) uppercase
  'zh-tw': 'zh', // Traditional Chinese (Taiwan) lowercase
  // Add more mappings here if needed
};

/**
 * Maps language codes to ElevenLabs-compatible language codes
 * @param language - The original language code
 * @returns The ElevenLabs-compatible language code
 */
const mapLanguageForElevenLabs = (language: string): string => {
  return ELEVENLABS_LANGUAGE_MAP[language] || language;
};

/**
 * Maps language codes to OpenAI-compatible language codes
 * @param language - The original language code
 * @returns The OpenAI-compatible language code
 */
const mapLanguageForOpenAI = (language: string): string => {
  return OPENAI_LANGUAGE_MAP[language] || language;
};

export function useVoiceEngine(
  language: string,
  preferredEngine?: VoiceEngine,
): VoiceEngineHook {
  // Determine which engine to use based on language and preferred engine
  const engine: VoiceEngine = useMemo(() => {
    // If preferredEngine is provided, use it
    if (preferredEngine) {
      console.log('🔧 Using preferred engine:', preferredEngine);
      return preferredEngine;
    }

    // Check if language requires OpenAI
    if (OPENAI_REQUIRED_LANGUAGES.includes(language?.toLowerCase())) {
      console.log('🇹🇭 Thai language detected, using OpenAI');
      return 'openai';
    }

    // Special logging for Chinese languages
    if (
      language === 'cmn' ||
      language === 'yue' ||
      language === 'zh-tw' ||
      language === 'zh-TW' ||
      language === 'zh'
    ) {
      console.log('🇨🇳 Chinese language detected:', {
        language,
        selectedEngine: 'elevenlabs',
        willMapTo: 'zh',
        note: `${language} will be mapped to 'zh' for ElevenLabs compatibility`,
        timestamp: new Date().toISOString(),
      });
    }

    // Default to elevenlabs
    return 'elevenlabs';
  }, [preferredEngine, language]);

  console.log('🎮 Voice Engine Selection:', {
    language,
    preferredEngine,
    selectedEngine: engine,
    timestamp: new Date().toISOString(),
  });
  // ElevenLabs
  const elevenLabs = useConversation();

  // Log ElevenLabs status changes
  useEffect(() => {
    if (engine === 'elevenlabs' && elevenLabs.status) {
      console.log('🔌 ElevenLabs status:', {
        status: elevenLabs.status,
        timestamp: new Date().toISOString(),
      });
    }
  }, [engine, elevenLabs.status]);

  // State
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [isPaused, setPaused] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Refs
  const callbacksRef = useRef<VoiceEngineCallbacks>({});
  const wsRef = useRef<WebSocket | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const sessionIdRef = useRef<string>('');

  // Initialize LiveKit with callbacks
  const liveKit = useLiveKit({
    onConnectionChange: (isConnected) => {
      setStatus(isConnected ? 'connected' : 'disconnected');
      if (isConnected) {
        callbacksRef.current.onConnect?.();
      }
    },
    metadata: undefined, // Add missing required property
    onTranscriptUpdate: (_segments) => {
      // Handle transcript updates if needed
    },
    onAgentSpeaking: (speaking) => {
      setIsSpeaking(speaking);
      callbacksRef.current.onAgentSpeaking?.(speaking);
    },
  });

  // Cleanup function
  const cleanup = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current.remove();
      audioElementRef.current = null;
    }

    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current?.close().catch(console.error);
      audioContextRef.current = null;
    }

    if (dataChannelRef.current?.readyState === 'open') {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current?.connectionState !== 'closed') {
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      wsRef.current = null;
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    sessionIdRef.current = ''; // Reset session ID
    callbacksRef.current = {}; // Reset callbacks
    setIsRecording(false);
    setIsSpeaking(false);
    setStatus('disconnected');
  }, []);

  // OpenAI Audio Playback - PCM16 format
  const playAudioQueue = useCallback(async () => {
    if (
      !audioContextRef.current ||
      isPlayingRef.current ||
      audioQueueRef.current.length === 0
    ) {
      return;
    }

    isPlayingRef.current = true;
    setIsSpeaking(true);

    while (audioQueueRef.current.length > 0 && !isPaused) {
      const pcm16Data = audioQueueRef.current.shift();
      if (!pcm16Data) continue;

      try {
        // Convert PCM16 to Float32 for Web Audio API
        const pcm16 = new Int16Array(pcm16Data);
        const float32 = new Float32Array(pcm16.length);

        for (let i = 0; i < pcm16.length; i++) {
          float32[i] = pcm16[i] / 32768.0; // Convert to -1.0 to 1.0 range
        }

        // Create audio buffer
        const audioBuffer = audioContextRef.current.createBuffer(
          1, // mono
          float32.length,
          24000, // sample rate
        );

        // Copy the float32 data to the audio buffer
        audioBuffer.copyToChannel(float32, 0);

        // Play the buffer
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);

        await new Promise<void>((resolve) => {
          source.onended = () => resolve();
          source.start();
        });
      } catch (err) {
        console.error('Error playing audio:', err);
      }
    }

    isPlayingRef.current = false;
    setIsSpeaking(false);
  }, [isPaused]);

  // OpenAI WebRTC Data Channel Message Handler
  // Handles transcript events elegantly - only final, most accurate transcripts are shown:
  // - User transcripts: input_audio_transcription.completed (from gpt-4o-transcribe)
  // - AI transcripts: response.audio_transcript.done (complete spoken response)
  const handleOpenAIDataChannelMessage = useCallback((event: MessageEvent) => {
    try {
      const response = JSON.parse(event.data);

      console.log('🔵 OpenAI Response:', response.type);
      callbacksRef.current.onDebug?.(response);

      switch (response.type) {
        case 'session.created':
          console.log('✅ OpenAI session created');
          sessionIdRef.current = response.session.id;
          break;

        case 'session.updated':
          console.log('✅ OpenAI session updated');
          break;

        case 'conversation.item.created':
          // Handle assistant messages only - these come from text content
          if (
            response.item.role === 'assistant' &&
            response.item.content?.[0]?.text
          ) {
            callbacksRef.current.onMessage?.({
              source: 'ai',
              message: response.item.content[0].text,
            });
          }
          // User messages are handled in input_audio_transcription.completed for final accuracy
          break;

        case 'conversation.item.input_audio_transcription.completed':
          // FINAL user speech transcription from gpt-4o-transcribe - most accurate
          // Only process if there's actual meaningful content (not empty or whitespace)
          if (response.transcript && response.transcript.trim().length > 0) {
            // Additional validation: check if item_id exists and this is truly a user input
            const isValidUserTranscript =
              response.item_id && response.transcript.trim().length > 2;

            if (isValidUserTranscript) {
              console.log('👤 User final transcript (gpt-4o-transcribe):', {
                transcript: response.transcript,
                item_id: response.item_id,
                content_index: response.content_index,
              });
              callbacksRef.current.onMessage?.({
                source: 'user',
                message: response.transcript,
              });
              callbacksRef.current.onTranscript?.(response.transcript, true);
            } else {
              console.log('⚠️ Ignoring invalid/empty user transcript:', {
                transcript: response.transcript,
                item_id: response.item_id,
                reason: !response.item_id ? 'no item_id' : 'too short',
              });
            }
          }
          break;

        case 'response.audio_transcript.delta':
          // Intermediate AI transcript chunks - ignore for clean final transcripts
          // Just log for debugging
          if (response.delta) {
            console.log('🤖 AI speaking (delta):', response.delta);
          }
          break;

        case 'response.audio_transcript.done':
          // FINAL AI transcript - most accurate, complete response
          if (response.transcript && response.transcript.trim()) {
            console.log('🤖 AI final transcript:', {
              transcript: response.transcript,
              response_id: response.response_id,
              item_id: response.item_id,
            });
            callbacksRef.current.onMessage?.({
              source: 'ai',
              message: response.transcript,
            });
          }
          break;

        case 'response.audio.delta':
          // Audio is now handled through WebRTC media stream
          console.log('🔊 Audio delta received (handled by WebRTC)');
          break;

        case 'response.audio.done':
          setIsSpeaking(false);
          break;

        case 'response.created':
          console.log('📢 Response created:', response);
          setIsSpeaking(true);
          break;

        case 'error': {
          const errorMsg = response.error?.message || 'Unknown error';
          console.error('❌ OpenAI Error:', errorMsg);
          setError(new Error(errorMsg));
          callbacksRef.current.onError?.(response.error);
          break;
        }

        default:
          // Log any unhandled event types for debugging
          console.log('📥 Unhandled OpenAI event:', response.type, response);
          break;
      }
    } catch (err) {
      console.error('Failed to parse OpenAI message:', err);
    }
  }, []);

  // Start OpenAI Session with WebRTC
  const startOpenAI = useCallback(
    async (
      config: VoiceEngineConfig,
      callbacks: VoiceEngineCallbacks,
    ): Promise<string> => {
      try {
        console.log('🎯 OpenAI WebRTC Configuration:', {
          originalLanguage: config.language,
          willMapTo: mapLanguageForOpenAI(config.language || 'en'),
          timestamp: new Date().toISOString(),
        });

        // Ensure any previous session is fully cleaned up before starting new one
        if (
          peerConnectionRef.current ||
          dataChannelRef.current ||
          mediaStreamRef.current
        ) {
          console.log(
            '⚠️ Found lingering connection, cleaning up before starting new session',
          );
          cleanup();
          // Give a small delay to ensure cleanup completes
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        setStatus('connecting');
        setError(null);
        callbacksRef.current = callbacks;

        const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
        if (!apiKey) {
          throw new Error(
            'OpenAI API key not configured. Set VITE_OPENAI_API_KEY in your environment.',
          );
        }

        // Get ephemeral token for WebRTC connection from backend
        // Get ephemeral token for WebRTC connection from backend
        let tokenResponse;
        try {
          tokenResponse = await api.post('realtime/api/get-ephemeral-token');
        } catch (fetchError) {
          console.error('❌ Failed to fetch ephemeral token:', {
            error: fetchError,
            message:
              fetchError instanceof Error
                ? fetchError.message
                : 'Unknown error',
            apiUrl: 'realtime/api/get-ephemeral-token',
            timestamp: new Date().toISOString(),
          });

          // Provide more specific error message
          if (
            fetchError instanceof TypeError &&
            fetchError.message === 'Failed to fetch'
          ) {
            throw new Error(
              'Network error: Unable to connect to the server. Please check your internet connection and try again.',
            );
          }
          throw new Error(
            `Failed to get authentication token: ${
              fetchError instanceof Error ? fetchError.message : 'Network error'
            }`,
          );
        }

        const { token: ephemeralKey } = (await tokenResponse.json()) as {
          token: string;
        };

        if (!ephemeralKey) {
          throw new Error(
            'Invalid response: No authentication token received from server',
          );
        }

        // Create RTCPeerConnection
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        });
        peerConnectionRef.current = pc;

        // Clean up any existing audio element from previous session
        if (audioElementRef.current) {
          audioElementRef.current.pause();
          audioElementRef.current.srcObject = null;
          audioElementRef.current.remove();
        }

        // Set up audio element for playback
        const audioEl = document.createElement('audio');
        audioEl.autoplay = true;
        audioElementRef.current = audioEl;

        pc.ontrack = (e) => {
          console.log('🔊 Received audio track from OpenAI');
          audioEl.srcObject = e.streams[0];
          setIsSpeaking(true);
        };

        // Get user media with advanced noise cancellation and echo cancellation
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true, // Remove echo from speakers
            noiseSuppression: true, // Suppress background noise
            autoGainControl: true, // Normalize audio levels
            sampleRate: 24000, // Match OpenAI Realtime API expected sample rate
            channelCount: 1, // Mono audio
          },
        });
        mediaStreamRef.current = stream;
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        setIsRecording(true);

        // Create data channel for events
        const dc = pc.createDataChannel('oai-events', {
          ordered: true,
        });
        dataChannelRef.current = dc;

        // Map language to OpenAI-compatible format
        const mappedLanguage = mapLanguageForOpenAI(config.language || 'en');

        console.log('🌐 Mapped language for OpenAI:', mappedLanguage);
        console.log('🔗 Instructions:', config.prompt);

        dc.onopen = () => {
          console.log('📡 Data channel opened');

          // Send session configuration with instructions from config.prompt
          // Use voice from config if provided, otherwise use default
          const sessionConfig = {
            type: 'session.update',
            session: {
              modalities: ['text', 'audio'],
              instructions: config.prompt || 'You are a helpful AI assistant.',
              voice: config.voiceId || OPENAI_CONFIG.voice,
              input_audio_format: 'pcm16',
              output_audio_format: 'pcm16',
              input_audio_transcription: {
                model: 'gpt-4o-transcribe',
                language: mappedLanguage,
              },
              turn_detection: OPENAI_CONFIG.turnDetection,
              temperature: OPENAI_CONFIG.temperature,
              max_response_output_tokens: OPENAI_CONFIG.maxTokens,
            },
          };

          console.log('📋 Sending session config with instructions:', {
            model: OPENAI_CONFIG.model,
            voice: config.voiceId || OPENAI_CONFIG.voice,
            transcriptionModel: 'gpt-4o-transcribe',
            language: mappedLanguage,
            turnDetection: {
              type: OPENAI_CONFIG.turnDetection.type,
              interrupt_response:
                OPENAI_CONFIG.turnDetection.interrupt_response,
              threshold: OPENAI_CONFIG.turnDetection.threshold,
              silence_duration_ms:
                OPENAI_CONFIG.turnDetection.silence_duration_ms,
            },
            instructionsLength: config.prompt?.length || 0,
            instructionsPreview: config.prompt?.substring(0, 100) + '...',
            temperature: OPENAI_CONFIG.temperature,
          });
          dc.send(JSON.stringify(sessionConfig));

          // Trigger AI to speak first with greeting (if firstMessage is provided)
          // Wait a brief moment for session to be fully ready before triggering
          if (config.firstMessage) {
            console.log('📨 Triggering AI to speak first with greeting');

            // Small delay to ensure session is fully initialized
            setTimeout(() => {
              if (dc.readyState === 'open') {
                // Trigger AI to generate and speak the initial greeting
                // The AI will use the instructions which contain the exact greeting to use
                dc.send(
                  JSON.stringify({
                    type: 'response.create',
                    response: {
                      modalities: ['text', 'audio'],
                      // Explicitly set input to empty to avoid any confusion
                      input: [],
                    },
                  }),
                );
                console.log('✅ Initial greeting trigger sent');
              }
            }, 100);
          }

          // COMMENTED OUT - old approach for reference
          //         if (config.firstMessage) {
          //           console.log('📨 Sending initial message:', config.firstMessage);

          //           dc.send(
          //             JSON.stringify({
          //               type: 'conversation.item.create',
          //               item: {
          //                 type: 'message',
          //                 role: 'assistant',
          //                 content: [
          //                   {
          //                     type: 'text',
          //                     text: config.firstMessage,
          //                   },
          //                 ],
          //               },
          //             }),
          //           );

          //           // Request response with audio
          //           const languageInstructions = {
          //             th: 'Please respond in Thai language.',
          //             cmn: 'Please respond in Mandarin Chinese.',
          //             yue: 'Please respond in Cantonese.',
          //           };

          //           dc.send(
          //             JSON.stringify({
          //               type: 'response.create',
          //               response: {
          //                 modalities: ['text', 'audio'],
          //                 instructions: `You are the CLIENT receiving a sales call.
          // ${
          //   languageInstructions[config.language] ||
          //   `Please respond in ${config.language}
          // language.`
          // }`,
          //               },
          //             }),
          //           );
          //         }

          setStatus('connected');
          callbacksRef.current.onConnect?.();
        };

        dc.onmessage = handleOpenAIDataChannelMessage;

        dc.onerror = (error) => {
          console.error('Data channel error:', error);
          setError(new Error('Data channel error'));
          callbacksRef.current.onError?.(error);
        };

        // Create offer
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Send offer to OpenAI and get answer
        let sdpResponse;
        try {
          sdpResponse = await fetch(
            `https://api.openai.com/v1/realtime?model=${OPENAI_CONFIG.model}`,
            {
              method: 'POST',
              body: offer.sdp,
              headers: {
                Authorization: `Bearer ${ephemeralKey}`,
                'Content-Type': 'application/sdp',
              },
            },
          );
        } catch (sdpError) {
          console.error('❌ Failed to exchange SDP with OpenAI:', {
            error: sdpError,
            message:
              sdpError instanceof Error ? sdpError.message : 'Unknown error',
            timestamp: new Date().toISOString(),
          });

          if (
            sdpError instanceof TypeError &&
            sdpError.message === 'Failed to fetch'
          ) {
            throw new Error(
              'Network error: Unable to connect to OpenAI. This might be due to firewall restrictions or regional limitations.',
            );
          }
          throw new Error(
            `Failed to establish WebRTC connection: ${
              sdpError instanceof Error ? sdpError.message : 'Network error'
            }`,
          );
        }

        if (!sdpResponse.ok) {
          const errorText = await sdpResponse
            .text()
            .catch(() => 'No error details');
          console.error('❌ OpenAI SDP exchange failed:', {
            status: sdpResponse.status,
            statusText: sdpResponse.statusText,
            errorDetails: errorText,
            timestamp: new Date().toISOString(),
          });
          throw new Error(
            `OpenAI connection failed (${sdpResponse.status}): ${sdpResponse.statusText}`,
          );
        }

        const answerSdp = await sdpResponse.text();
        await pc.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        });

        // Handle connection state changes
        pc.onconnectionstatechange = () => {
          console.log('🔌 Connection state:', pc.connectionState);
          if (
            pc.connectionState === 'failed' ||
            pc.connectionState === 'disconnected'
          ) {
            setStatus('disconnected');
            cleanup();
          }
        };

        return sessionIdRef.current || 'openai-webrtc-session';
      } catch (err) {
        console.error('❌ OpenAI WebRTC error:', err);
        setError(err as Error);
        setStatus('error');
        cleanup();
        throw err;
      }
    },
    [handleOpenAIDataChannelMessage, cleanup],
  );

  // Start LiveKit Session
  const startLiveKit = useCallback(
    async (
      config: VoiceEngineConfig,
      callbacks: VoiceEngineCallbacks,
    ): Promise<string> => {
      if (!config.roomName || !config.participantName) {
        throw new Error('LiveKit requires roomName and participantName');
      }

      // Update callbacks ref
      callbacksRef.current = callbacks;
      setStatus('connecting');

      try {
        const sessionId = await liveKit.startVoiceSession(
          {
            ...config,
            roomName: config.roomName!,
            participantName: config.participantName!,
          },
          callbacks,
        );

        setIsRecording(true);
        return sessionId;
      } catch (error) {
        setStatus('error');
        setError(error instanceof Error ? error : new Error(String(error)));
        throw error;
      }
    },
    [liveKit],
  );

  // Start ElevenLabs Session
  const startElevenLabs = useCallback(
    async (
      config: VoiceEngineConfig,
      callbacks: VoiceEngineCallbacks,
    ): Promise<string> => {
      // Map language to ElevenLabs-compatible format
      const mappedLanguage = mapLanguageForElevenLabs(config.language || 'en');

      console.log('🎯 ElevenLabs Configuration:', {
        agentId: config.agentId,
        originalLanguage: config.language,
        mappedLanguage,
        voiceId: config.voiceId,
        prompt: config.prompt?.substring(0, 200) + '...',
        firstMessage: config.firstMessage,
        timestamp: new Date().toISOString(),
      });
      console.log('config.prompt:', config.prompt);

      const connectionType =
        config.scenario === 'scb-6'
          ? ('websocket' as const)
          : ('webrtc' as const);

      console.log('tts config:', {
        tts: {
          ...(config.voiceId && { voiceId: config.voiceId }),
          ...(config.ttsConfig?.stability !== undefined && {
            stability: config.ttsConfig.stability,
          }),
          ...(config.ttsConfig?.similarityBoost !== undefined && {
            similarity_boost: config.ttsConfig.similarityBoost,
          }),
          ...(config.ttsConfig?.style !== undefined && {
            style: config.ttsConfig.style,
          }),
          ...(config.ttsConfig?.speed !== undefined && {
            speed: config.ttsConfig.speed,
          }),
          ...(config.ttsConfig?.useSpeakerBoost !== undefined && {
            use_speaker_boost: config.ttsConfig.useSpeakerBoost,
          }),
        },
      });

      const sessionConfig = {
        agentId: config.agentId!,
        connectionType,
        overrides: {
          agent: {
            prompt: { prompt: config.prompt },
            firstMessage: config.firstMessage,
            language: mappedLanguage as Language,
          },
          ...(config.voiceId || config.ttsConfig
            ? {
                tts: {
                  ...(config.voiceId && { voiceId: config.voiceId }),
                  ...(config.ttsConfig?.stability !== undefined && {
                    stability: config.ttsConfig.stability,
                  }),
                  ...(config.ttsConfig?.similarityBoost !== undefined && {
                    similarity_boost: config.ttsConfig.similarityBoost,
                  }),
                  ...(config.ttsConfig?.style !== undefined && {
                    style: config.ttsConfig.style,
                  }),
                  ...(config.ttsConfig?.speed !== undefined && {
                    speed: config.ttsConfig.speed,
                  }),
                  ...(config.ttsConfig?.useSpeakerBoost !== undefined && {
                    use_speaker_boost: config.ttsConfig.useSpeakerBoost,
                  }),
                },
              }
            : {}),
        },
        onConnect: callbacks.onConnect,
        onError: callbacks.onError,
        onMessage: callbacks.onMessage,
        onDebug: callbacks.onDebug,
      };

      console.log('📦 ElevenLabs Session Config being sent:', {
        ...sessionConfig,
        overrides: {
          ...sessionConfig.overrides,
          agent: {
            ...sessionConfig.overrides.agent,
            language: mappedLanguage,
            prompt: {
              prompt:
                sessionConfig.overrides.agent.prompt.prompt?.substring(0, 200) +
                '...',
            },
          },
        },
        timestamp: new Date().toISOString(),
      });

      try {
        const conversationId = await elevenLabs.startSession(sessionConfig);

        console.log('✅ ElevenLabs session started:', {
          conversationId,
          originalLanguage: config.language,
          mappedLanguage,
          timestamp: new Date().toISOString(),
        });

        return conversationId;
      } catch (error) {
        console.error('❌ ElevenLabs session start error:', {
          error,
          originalLanguage: config.language,
          mappedLanguage,
          config: sessionConfig,
          timestamp: new Date().toISOString(),
        });
        throw error;
      }
    },
    [elevenLabs],
  );

  // Main Interface
  const start = useCallback(
    async (
      config: VoiceEngineConfig,
      callbacks: VoiceEngineCallbacks = {},
    ): Promise<string> => {
      console.log(
        `🚀 Starting ${engine} voice engine for language: ${config.language}`,
      );

      // Update callbacks ref
      callbacksRef.current = callbacks;

      try {
        setStatus('connecting');
        setError(null);

        if (engine === 'openai') {
          const result = await startOpenAI(config, callbacks);
          setStatus('connected');
          return result;
        }

        if (engine === 'livekit') {
          const result = await startLiveKit(config, callbacks);
          return result;
        }

        // Default to ElevenLabs
        const result = await startElevenLabs(config, callbacks);
        setStatus('connected');
        return result;
      } catch (error) {
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        setStatus('error');
        setError(errorObj);
        callbacks.onError?.(errorObj);
        throw errorObj;
      }
    },
    [engine, startOpenAI, startLiveKit, startElevenLabs],
  );

  const stop = useCallback(async () => {
    console.log(`🛑 Stopping ${engine} voice engine`);

    try {
      if (engine === 'openai') {
        // Send end session message before cleanup
        if (dataChannelRef.current?.readyState === 'open') {
          dataChannelRef.current.send(
            JSON.stringify({
              type: 'input_audio_buffer.clear',
            }),
          );
        }
        cleanup();
      } else if (engine === 'livekit') {
        await liveKit.stopVoiceSession();
      } else {
        await elevenLabs.endSession();
      }
      setStatus('disconnected');
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      setError(errorObj);
      callbacksRef.current.onError?.(errorObj);
      throw errorObj;
    }
  }, [engine, elevenLabs, liveKit, cleanup]);

  const pause = useCallback(() => {
    try {
      if (engine === 'openai') {
        // Pause microphone tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = false;
          });
        }
        setPaused(true);
        console.log('⏸️ Paused OpenAI audio capture');
      } else if (engine === 'livekit') {
        liveKit.stopMicrophone();
        console.log('⏸️ Paused LiveKit microphone');
      }
      // ElevenLabs doesn't support pause
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      setError(errorObj);
      callbacksRef.current.onError?.(errorObj);
      console.error('Error pausing audio:', error);
    }
  }, [engine, liveKit]);

  const resume = useCallback(() => {
    try {
      if (engine === 'openai') {
        // Resume microphone tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = true;
          });
        }
        setPaused(false);
        console.log('▶️ Resumed OpenAI audio capture');
      } else if (engine === 'livekit') {
        liveKit.startMicrophone();
        console.log('▶️ Resumed LiveKit microphone');
      }
      // ElevenLabs doesn't support resume
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      setError(errorObj);
      callbacksRef.current.onError?.(errorObj);
      console.error('Error resuming audio:', error);
    }
  }, [engine, liveKit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // Core functions
    start,
    stop,
    pause,
    resume,
    startMicrophone: engine === 'livekit' ? liveKit.startMicrophone : undefined,

    // State
    status:
      engine === 'openai'
        ? status
        : engine === 'livekit'
          ? liveKit.isConnected
            ? 'connected'
            : 'disconnected'
          : (elevenLabs.status as ConnectionStatus),
    engine,
    isRecording:
      engine === 'openai'
        ? isRecording
        : engine === 'livekit'
          ? liveKit.isMicrophoneActive
          : (elevenLabs as any).isRecording || false,
    isSpeaking:
      engine === 'openai'
        ? isSpeaking
        : engine === 'livekit'
          ? liveKit.isAgentSpeaking
          : elevenLabs.isSpeaking,
    isPaused,
    error:
      engine === 'livekit'
        ? liveKit.error
          ? new Error(liveKit.error)
          : null
        : error,

    // Utilities
    getCurrentEngine: () => engine,
    isEngineSupported: (lang: string) => {
      if (OPENAI_REQUIRED_LANGUAGES.includes(lang)) {
        return !!import.meta.env.VITE_OPENAI_API_KEY;
      }
      if (engine === 'livekit') {
        return !!import.meta.env.VITE_LIVEKIT_URL;
      }
      return true;
    },
  };
}

// Utility functions
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
